import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { FaFilter, FaPlus } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { motion } from "framer-motion";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";

const Grievances = () => {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGrievances();
  }, [currentPage, itemsPerPage]);

  const fetchGrievances = () => {
    setLoading(true);

    fetch(`${apiurl}/api/grievance/get?page=${currentPage}&limit=${itemsPerPage}`)
      .then((res) => res.json())
      .then((data) => {
        setGrievances(data.grievances || []);
        setTotalItems(data.pagination?.totalItems || 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching grievances:", err);
        setLoading(false);
      });
  };

  const handleView = (id) => {
    navigate(`/grievances/${id}/manage`);
  };

  const filteredGrievances = grievances.filter((g) => {
    const searchLower = search.toLowerCase();
    return (
      g?.title?.toLowerCase().includes(searchLower) ||
      g?.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleLimitChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const listVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.05, duration: 0.3 },
    }),
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
            <input
              type="text"
              placeholder="Search grievance by title or description"
              className="p-2 border rounded-lg w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
              <select
                className="p-2 border rounded-lg focus:outline-none"
                value={itemsPerPage}
                onChange={handleLimitChange}
              >
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
              </select>

              <button
                onClick={() => setFilterModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-blue-500 hover:bg-blue-600"
              >
                <FaFilter /> Filter
              </button>

              <Link
                to="/grievances/new"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-indigo-500 hover:bg-indigo-600"
              >
                <FaPlus /> Add Grievance
              </Link>
            </div>
          </div>

          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200 text-sm text-gray-700">
                    <th className="p-3 text-left">ID</th>
                    <th className="p-3 text-left">Employee ID</th>
                    <th className="p-3 text-left">Employee Name</th>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Category</th>
                    <th className="p-3 text-left">Priority</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-left">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredGrievances.map((g, index) => (
                    <motion.tr
                      key={g._id}
                      variants={listVariants}
                      initial="hidden"
                      animate="visible"
                      custom={index}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="p-3 text-sm">{g.id}</td>
                      <td className="p-3 text-sm">{g.requester.employeeCode}</td>
                      <td className="p-3 text-sm">{g.requester.userName}</td>
                      <td className="p-3 text-sm">{g.title}</td>
                      <td className="p-3 text-sm">{g.category}</td>
                      <td className="p-3 text-sm capitalize">{g.priority}</td>
                      <td className="p-3 text-sm">
                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-600 text-xs">
                          {g.status}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(g.createdAt).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleView(g._id)}
                          className="bg-green-500 text-white text-xs px-3 py-1 rounded hover:bg-green-600"
                        >
                          Manage
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={(p) => setCurrentPage(p)}
          />

          {filterModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
                <h2 className="text-lg font-semibold mb-4">Filter Grievances</h2>

                <label className="block text-sm mb-1">Start Date</label>
                <DatePicker className="w-full p-2 border rounded mb-3" selected={startDate} onChange={setStartDate} />

                <label className="block text-sm mb-1">End Date</label>
                <DatePicker className="w-full p-2 border rounded mb-3" selected={endDate} onChange={setEndDate} />

                <div className="flex justify-end gap-2">
                  <button className="px-4 py-2 rounded bg-gray-300" onClick={() => setFilterModalOpen(false)}>
                    Cancel
                  </button>
                  <button className="px-4 py-2 rounded bg-green-600 text-white" onClick={fetchGrievances}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default Grievances;